import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { defaultSEO, pagesSEO } from './seoData';

const SEO = ({
  pageKey,
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  ogUrl,
  ogType = 'website',
  twitterCard = 'summary_large_image',
}) => {
  const config = (pageKey && pagesSEO[pageKey]) || {};

  const resolvedTitle = title || config.title || defaultSEO.title;
  const resolvedDescription = description || config.description || defaultSEO.description;
  const resolvedKeywords = keywords || config.keywords || defaultSEO.keywords;
  const resolvedCanonical = canonical || config.canonical || defaultSEO.siteUrl;
  const resolvedOgImage = ogImage || defaultSEO.ogImage;
  const resolvedOgTitle = ogTitle || resolvedTitle;
  const resolvedOgDescription = ogDescription || resolvedDescription;

  // Direct DOM synchronization: Guarantees 1 single <title> tag and updates meta in DOM
  useEffect(() => {
    if (resolvedTitle) {
      document.title = resolvedTitle;
    }

    // Clean up duplicate <title> tags so ONLY ONE remains in <head>
    const allTitles = document.head.querySelectorAll('title');
    if (allTitles.length > 1) {
      allTitles.forEach((t, index) => {
        if (index > 0) t.remove();
      });
    }
    if (allTitles[0]) {
      allTitles[0].textContent = resolvedTitle;
    }

    const setSingleMeta = (selector, attr, key, val) => {
      if (!val) return;
      const elements = document.head.querySelectorAll(selector);
      if (elements.length > 1) {
        elements.forEach((el, index) => {
          if (index > 0) el.remove();
        });
      }
      let target = elements[0];
      if (!target) {
        target = document.createElement('meta');
        target.setAttribute(attr, key);
        document.head.appendChild(target);
      }
      target.setAttribute('content', val);
    };

    setSingleMeta('meta[name="title"]', 'name', 'title', resolvedTitle);
    setSingleMeta('meta[name="description"]', 'name', 'description', resolvedDescription);
    if (resolvedKeywords) {
      setSingleMeta('meta[name="keywords"]', 'name', 'keywords', resolvedKeywords);
    }

    // Open Graph
    setSingleMeta('meta[property="og:title"]', 'property', 'og:title', resolvedOgTitle);
    setSingleMeta('meta[property="og:description"]', 'property', 'og:description', resolvedOgDescription);
    setSingleMeta('meta[property="og:image"]', 'property', 'og:image', resolvedOgImage);
    setSingleMeta('meta[property="og:type"]', 'property', 'og:type', ogType);
    if (resolvedCanonical) {
      setSingleMeta('meta[property="og:url"]', 'property', 'og:url', ogUrl || resolvedCanonical);
    }

    // Twitter
    setSingleMeta('meta[name="twitter:card"]', 'name', 'twitter:card', twitterCard);
    setSingleMeta('meta[name="twitter:title"]', 'name', 'twitter:title', resolvedOgTitle);
    setSingleMeta('meta[name="twitter:description"]', 'name', 'twitter:description', resolvedOgDescription);
    setSingleMeta('meta[name="twitter:image"]', 'name', 'twitter:image', resolvedOgImage);
    if (resolvedCanonical) {
      setSingleMeta('meta[name="twitter:url"]', 'name', 'twitter:url', ogUrl || resolvedCanonical);
    }

    // Canonical link
    if (resolvedCanonical) {
      const canonicals = document.head.querySelectorAll('link[rel="canonical"]');
      if (canonicals.length > 1) {
        canonicals.forEach((el, index) => {
          if (index > 0) el.remove();
        });
      }
      let targetLink = canonicals[0];
      if (!targetLink) {
        targetLink = document.createElement('link');
        targetLink.setAttribute('rel', 'canonical');
        document.head.appendChild(targetLink);
      }
      targetLink.setAttribute('href', resolvedCanonical);
    }
  }, [
    resolvedTitle,
    resolvedDescription,
    resolvedKeywords,
    resolvedCanonical,
    resolvedOgTitle,
    resolvedOgDescription,
    resolvedOgImage,
    ogType,
    ogUrl,
    twitterCard,
  ]);

  return (
    <Helmet>
      {/* Primary HTML Meta Tags */}
      <title>{resolvedTitle}</title>
      <meta name="title" content={resolvedTitle} />
      <meta name="description" content={resolvedDescription} />
      {resolvedKeywords && <meta name="keywords" content={resolvedKeywords} />}
      {resolvedCanonical && <link rel="canonical" href={resolvedCanonical} />}

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={resolvedOgTitle} />
      <meta property="og:description" content={resolvedOgDescription} />
      <meta property="og:image" content={resolvedOgImage} />
      {resolvedCanonical && <meta property="og:url" content={ogUrl || resolvedCanonical} />}

      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={resolvedOgTitle} />
      <meta name="twitter:description" content={resolvedOgDescription} />
      <meta name="twitter:image" content={resolvedOgImage} />
      {resolvedCanonical && <meta name="twitter:url" content={ogUrl || resolvedCanonical} />}
    </Helmet>
  );
};

export default SEO;
